import { useState } from "react";
import {
  X, Mail, MessageCircle, Facebook, Twitter, Check, Share2,
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

export const ShareModal = ({ isOpen, onClose, recommendations }) => {
  const [copied, setCopied] = useState(false);
  
  if (!isOpen) return null;
  
  const shareText = `Check out my ExpatMatch results! My top country matches for relocation:\n\n${
    recommendations.slice(0, 3).map((r, i) => `${i + 1}. ${r.country_name} (${r.match_score}% match)`).join('\n')
  }\n\nFind your perfect country at ExpatMatch!`;
  
  const shareUrl = window.location.origin;
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);
  
  const shareLinks = [
    {
      name: "Email",
      icon: Mail,
      color: "bg-gray-600",
      url: `mailto:?subject=${encodeURIComponent("My ExpatMatch Results - Top Countries for Relocation")}&body=${encodedText}%0A%0A${encodedUrl}`,
      testId: "share-email"
    },
    {
      name: "Text/SMS",
      icon: MessageCircle,
      color: "bg-green-600",
      url: `sms:?body=${encodedText}%20${encodedUrl}`,
      testId: "share-sms"
    },
    {
      name: "Facebook",
      icon: Facebook,
      color: "bg-blue-600",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      testId: "share-facebook"
    },
    {
      name: "X (Twitter)",
      icon: Twitter,
      color: "bg-black",
      url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      testId: "share-twitter"
    },
    {
      name: "Pinterest",
      icon: () => (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
        </svg>
      ),
      color: "bg-red-600",
      url: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}`,
      testId: "share-pinterest"
    },
    {
      name: "Instagram",
      icon: () => (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      ),
      color: "bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-600",
      onClick: () => {
        navigator.clipboard.writeText(shareText + "\n\n" + shareUrl);
        toast.success("Copied! Open Instagram and paste in your story or post");
      },
      testId: "share-instagram"
    }
  ];

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareText + "\n\n" + shareUrl);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-card rounded-2xl border border-primary/10 p-6 w-full max-w-md shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-primary"
          data-testid="close-share-modal"
        >
          <X className="h-5 w-5" />
        </button>
        
        <h2 className="text-xl font-bold text-primary mb-2">Share Your Results</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Let friends and family know about your top country matches!
        </p>
        
        {/* Preview */}
        <div className="bg-background rounded-lg p-4 mb-6 border border-primary/10">
          <p className="text-sm text-foreground/80 whitespace-pre-line">
            {shareText.slice(0, 150)}...
          </p>
        </div>
        
        {/* Share Buttons */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {shareLinks.map((link) => {
            const IconComponent = link.icon;
            return (
              <a
                key={link.name}
                href={link.url}
                target={link.url?.startsWith("mailto") || link.url?.startsWith("sms") ? "_self" : "_blank"}
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (link.onClick) {
                    e.preventDefault();
                    link.onClick();
                  }
                }}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl ${link.color} text-white hover:opacity-90 transition-opacity`}
                data-testid={link.testId}
              >
                {typeof IconComponent === 'function' ? <IconComponent /> : <IconComponent className="h-5 w-5" />}
                <span className="text-xs font-medium">{link.name}</span>
              </a>
            );
          })}
        </div>
        
        {/* Copy Link */}
        <Button
          onClick={copyToClipboard}
          variant="outline"
          className="w-full gap-2"
          data-testid="copy-share-link"
        >
          {copied ? "Copied!" : "Copy to Clipboard"}
        </Button>
      </div>
    </div>
  );
};

export default ShareModal;
